/**
 * Register a product, or edit one, in a dialog. Redirect URIs one per line, the
 * product's PUBLIC key as a JWKS. The backend validates every rule (https only,
 * exact URIs, no private keys); this form only parses the JSON.
 */

import { useState } from "react";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { Field, TextArea, TextInput } from "@/components/fields";
import { useReportError } from "@/components/states";
import { useSaveService } from "./hooks";
import type { Jwks, Service } from "./types";

const JWKS_HINT = '{"keys": [{"kty": "EC", "crv": "P-256", "kid": "qsafe-1", "x": "…", "y": "…"}]}';

function lines(text: string, split: RegExp) {
  return text.split(split).map((s) => s.trim()).filter(Boolean);
}

export function ServiceForm({ service, onDone }: { service: Service | null; onDone: () => void }) {
  const save = useSaveService();
  const reportError = useReportError();
  const [audience, setAudience] = useState(service?.audience ?? "");
  const [name, setName] = useState(service?.name ?? "");
  const [uris, setUris] = useState(service?.redirect_uris.join("\n") ?? "");
  const [keys, setKeys] = useState(service ? JSON.stringify(service.jwks, null, 2) : "");
  const [mayCall, setMayCall] = useState(service?.may_act_for.join(", ") ?? "");
  const [jsonError, setJsonError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    let jwks: Jwks;
    try {
      jwks = JSON.parse(keys) as Jwks;
    } catch {
      setJsonError("That isn't valid JSON. Paste the whole JWKS, starting with {\"keys\": [...]}.");
      return;
    }
    setJsonError(null);
    try {
      await save.mutateAsync({
        isNew: service === null,
        payload: { audience, name, jwks, redirect_uris: lines(uris, /\n/), may_act_for: lines(mayCall, /[,\s]+/) },
      });
      onDone();
    } catch (err) {
      reportError(err);
    }
  }

  return (
    <form onSubmit={submit}>
      <Dialog
        open
        onClose={onDone}
        title={service ? `Edit ${service.name}` : "Register a service"}
        description="Each product proves who it is by signing its token requests with its own private key."
        footer={
          <>
            <Button onClick={onDone}>Cancel</Button>
            <Button type="submit" tone="primary" disabled={save.isPending}>
              {service ? "Save changes" : "Register service"}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Audience" hint="Also its client_id, e.g. qsafe. Can't change later.">
            <TextInput value={audience} onChange={(e) => setAudience(e.target.value)} disabled={service !== null}
              required placeholder="qsafe" className="font-mono" autoFocus />
          </Field>
          <Field label="Display name">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="qSafe" />
          </Field>
        </div>
        <Field label="Redirect URIs" hint="One per line, exact match. https only, except http://localhost.">
          <TextArea value={uris} onChange={(e) => setUris(e.target.value)} rows={3}
            placeholder="https://qsafe.qure.ai/api/v1/auth/callback" />
        </Field>
        <Field label="Public keys (JWKS)" hint="The public half only. A private key is refused.">
          <TextArea value={keys} onChange={(e) => setKeys(e.target.value)} rows={5} placeholder={JWKS_HINT}
            aria-invalid={jsonError !== null} />
        </Field>
        {jsonError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{jsonError}</p>}
        <Field label="May call for a user" hint="Audiences this service may exchange a user's token for, comma separated.">
          <TextInput value={mayCall} onChange={(e) => setMayCall(e.target.value)} placeholder="hoa, qsafe, ryuauth"
            className="font-mono" />
        </Field>
      </Dialog>
    </form>
  );
}
